/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

/** Utilities */
import formatDate from "../../../lib/formatDate";
import { decryptUrlData, encryptUrlData } from "../../../services/CryptoService";

/** Common Components */
import RenderTabs from "../../../components/ui/tabs/RenderTabs";
import Iconify from "../../../layouts/components/iconify/iconify";
import CardSection from "../../../components/ui/card/CardSection";
import DropdownSelect from "../../../components/ui/dropdown/DropDown";
import DatePickerComponent from "../../../components/finalForms/ui/inputs/datePicker/DatePicker";

/** Common Services & Data Files */
import ToastService from "../../../services/toastService/ToastService";
import { getTeamPlanningMessageType, isTeamPlanningSuccessMessage } from "../utils/teamPlanningResponse";

/** CSS Files */
import "../TaskPlan.scss";

/** Lazily Loaded Components */
import PlannedTasks from "../tabContent/plannedTasks/PlannedTasks";
import ResourceTaskPlanning from "../tabContent/resourcePlanning/ResourceTaskPlanning";
import PlanningTeamMembers from "../tabContent/planningTeamMembers/PlanningTeamMembers";
// import ProjectResourcePlanning from "../tabContent/projectResourcePlanning/ProjectResourcePlanning";

/** RTK Query */
import { useUpdateTeamPlanningMutation } from "../../../app/services/teamPlanningAPI";
import { useGetAllPlanningStatusListMutation } from "../../../app/services/planningStatusAPI";
import { useLazyGetResourceTotalPlanningCountQuery } from "../../../app/services/teamResourcePlanningAPI";
import PlanningReport from "../tabContent/planningReport/PlanningReport";

const TaskPlanTab = (props) => {

  let teamId = null;
  let planningId = null;
  let startDate = null;
  let endDate = null;
  let workingDays = null;
  let planningStatusId = null;

  const { id } = useParams();

  const teamData = id ? decryptUrlData(id) : null;

  if (teamData && typeof teamData === "object" && teamData !== null) {
    teamId = teamData.teamId;
    planningId = teamData.planningId;
    startDate = teamData.startDate;
    endDate = teamData.endDate;
    workingDays = teamData.workingDays;
    planningStatusId = teamData.planningStatusId;
  } else if (teamData) {
    teamId = teamData;
  }

  const [dataSource, setDataSource] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editedStartDate, setEditedStartDate] = useState(startDate);
  const [editedEndDate, setEditedEndDate] = useState(endDate);
  const [displayWorkingDays, setDisplayWorkingDays] = useState(workingDays);
  const [listData, setListData] = useState([])
  const [selectStatus, setSelectStatus] = useState();
  const [selectedStatusData, setSelectedStatusData] = useState(null);
  const [pendingTeamData, setPendingTeamData] = useState(null);
  // Bumped after mutations so tab content (e.g. Planning Report) can refetch.
  const [planningRefreshKey, setPlanningRefreshKey] = useState(0);

  const navigate = useNavigate();

  const [getResourceTotalPlanningCount, {
    isSuccess: isgetResourceTotalPlanningCountSuccess,
    data: isgetResourceTotalPlanningCountData,
  },] = useLazyGetResourceTotalPlanningCountQuery();

  const [updateTeamPlanning, {
    isSuccess: isTeamPlanningUpdated,
    data: TeamPlanningUpdateData,
  },] = useUpdateTeamPlanningMutation();

  const [getAllPlanningStatus, {
    isSuccess: isGetAllPlanningStatusSuccess,
    data: getAllPlanningStatusData
  },] = useGetAllPlanningStatusListMutation();

  const refreshPlanningKpis = () => {
    if (teamId && planningId) {
      getResourceTotalPlanningCount({ teamId, planningId });
    }
  };

  const notifyPlanningDataChanged = () => {
    setPlanningRefreshKey((key) => key + 1);
    refreshPlanningKpis();
  };

  useEffect(() => {
    if (isgetResourceTotalPlanningCountSuccess && isgetResourceTotalPlanningCountData) {
      setDataSource(isgetResourceTotalPlanningCountData)
    }
  }, [isgetResourceTotalPlanningCountSuccess, isgetResourceTotalPlanningCountData]);

  // Keep header week fields in sync when the encrypted URL payload changes after save.
  useEffect(() => {
    setEditedStartDate(startDate);
    setEditedEndDate(endDate);
    setDisplayWorkingDays(workingDays);
  }, [id, startDate, endDate, workingDays]);

  const getAllPlanningStatusList = () => {
    const request = {
      pagination: {
        pageNumber: 1,
        pageSize: 100,
      },
      filters: {
        searchText: "",
      },
      // teamId: teamId,
    };
    getAllPlanningStatus(request);
  };

  useEffect(() => {
    getAllPlanningStatusList();
  }, []);

  useEffect(() => {
    if (isGetAllPlanningStatusSuccess && getAllPlanningStatusData) {
      const statusData = getAllPlanningStatusData.dataSource.map((item) => ({
        value: item.planningStatusId,
        label: item.statusName,
        isDefault: item.isDefault,
        isComplete: item.isComplete,
      }));
      setListData(statusData);
      const defaultStatus = statusData.find((i) => i.isDefault === true);
      const selectedStatusId = planningStatusId ? planningStatusId : defaultStatus?.value;
      setSelectStatus(selectedStatusId);
      setSelectedStatusData(statusData.find((item) => item.value === selectedStatusId) || null);
    }
  }, [isGetAllPlanningStatusSuccess, getAllPlanningStatusData]);

  const isPlanComplete = teamData?.isComplete === true;
  // Do not fall back to totalWorkingHours — that compares capacity to productive % and
  // falsely triggers "Estimated hours exceed productive hours" when nothing is planned.
  const totalEstimatedHours = Number(
    dataSource?.totalEstimatedHours ?? dataSource?.totalEstimateHours ?? dataSource?.estimatedHours ?? 0
  );
  const isOverAllocated =
    totalEstimatedHours > 0 &&
    totalEstimatedHours > Number(dataSource?.totalProductiveHours || 0);

  const tabs = [
    {
      sMenuItemCaption: "Planned Task",
      icon: "material-symbols-light:task-outline",
      component: (
        <div className="mt-2">
          <PlannedTasks
            teamData={teamData}
            getResourceTotalPlanningCount={getResourceTotalPlanningCount}
            onPlanningDataChanged={notifyPlanningDataChanged}
            isReadOnly={isPlanComplete}
          />
        </div>
      ),
    },
    {
      sMenuItemCaption: "Task Planning",
      icon: "clarity:resource-pool-line",
      component: (
        <div className="mt-2">
          <ResourceTaskPlanning
            teamData={teamData}
            getResourceTotalPlanningCount={getResourceTotalPlanningCount}
            onPlanningDataChanged={notifyPlanningDataChanged}
            isReadOnly={isPlanComplete}
          />
        </div>
      ),
    },
    {
      sMenuItemCaption: "Resource Planning",
      icon: "si:projects-line",
      component: (
        <div className="mt-2">
          <CardSection
            cardTitle={"Planning Team Member"}
          >
            <PlanningTeamMembers
              planningId={planningId}
              teamId={teamId}
              getResourceTotalPlanningCount={getResourceTotalPlanningCount}
              onPlanningDataChanged={notifyPlanningDataChanged}
              isReadOnly={isPlanComplete}
            />
          </CardSection>
        </div>
      ),
    },
      {
      sMenuItemCaption: "Planning Report",
      icon: "si:projects-line",
      component: (
        <div className="mt-2">
           <PlanningReport
             planningId={planningId}
             teamId={teamId}
             refreshKey={planningRefreshKey}
             getResourceTotalPlanningCount={getResourceTotalPlanningCount}
           />
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (isTeamPlanningUpdated && TeamPlanningUpdateData) {
      const message = TeamPlanningUpdateData.errorMessage;
      const messageType = getTeamPlanningMessageType(message);
      ToastService[messageType](message);

      if (!isTeamPlanningSuccessMessage(message)) {
        const defaultStatus = listData.find((i) => i.isDefault === true);
        const selectedStatusId = planningStatusId ? planningStatusId : defaultStatus?.value;
        setSelectStatus(selectedStatusId);
        setSelectedStatusData(listData.find((item) => item.value === selectedStatusId) || null);
        setPendingTeamData(null);
        return;
      }

      setIsEditing(false);

      // Only rewrite the URL with the payload we just saved — never fall back to stale teamData.
      const updatedTeamData = pendingTeamData;
      if (!updatedTeamData) {
        refreshPlanningKpis();
        return;
      }

      setEditedStartDate(updatedTeamData.startDate);
      setEditedEndDate(updatedTeamData.endDate);
      setDisplayWorkingDays(updatedTeamData.workingDays);
      setSelectStatus(updatedTeamData.planningStatusId);
      setSelectedStatusData(
        listData.find((item) => item.value === updatedTeamData.planningStatusId) || selectedStatusData
      );
      setPendingTeamData(null);
      setPlanningRefreshKey((key) => key + 1);

      const encryptedTeamData = encryptUrlData(updatedTeamData);
      const currentPath = window.location.pathname;
      if (currentPath.toLowerCase().includes('/admin/')) {
        navigate(`/admin/teams/taskplan/${encryptedTeamData}`, { replace: true });
      } else {
        navigate(`/myteams/taskplan/${encryptedTeamData}`, { replace: true });
      }

      getResourceTotalPlanningCount({
        teamId: updatedTeamData.teamId,
        planningId: updatedTeamData.planningId,
      });
    }
  }, [isTeamPlanningUpdated, TeamPlanningUpdateData, pendingTeamData, listData]);

  const handleStatusChange = (option) => {
    if (!option) return;
    if (isPlanComplete && option.isComplete) {
      ToastService.warning("Select a non-complete status to reopen this planning.");
      return;
    }

    setSelectStatus(option.value);
    setSelectedStatusData(option);
    handleSave(option.value);
  };


  const handleEditClick = () => {
    setEditedStartDate(startDate);
    setEditedEndDate(endDate);
    setIsEditing(true);
  };


  const handleCancel = () => {
    setEditedStartDate(startDate);
    setEditedEndDate(endDate);
    setIsEditing(false);
  };

  const handleSave = (planningStatusId) => {
    if (!editedStartDate) {
      ToastService.error("Planning Week start date is required");
      return;
    }
    if (!editedEndDate) {
      ToastService.error("Planning Week end date is required");
      return;
    }
    if (!planningStatusId && !selectStatus) {
      ToastService.error("Planning Status is required");
      return;
    }

    const start = new Date(editedStartDate);
    const end = new Date(editedEndDate);

    if (start > end) {
      ToastService.error("Start date cannot be greater than end date");
      return;
    }

    let calculatedWorkingDays = 0;
    for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        calculatedWorkingDays++;
      }
    }

    if (calculatedWorkingDays <= 0) {
      ToastService.error("Working Days must be greater than 0");
      return;
    }

    const nextPlanningStatusId = planningStatusId ? planningStatusId : selectStatus;
    const nextStatusData = listData.find((item) => item.value === nextPlanningStatusId) || selectedStatusData;

    setDisplayWorkingDays(calculatedWorkingDays);
    setPendingTeamData({
      ...teamData,
      planningId,
      teamId,
      teamName: dataSource?.teamName || teamData?.teamName,
      startDate: start,
      endDate: end,
      workingDays: calculatedWorkingDays,
      planningStatusId: nextPlanningStatusId,
      isComplete: nextStatusData?.isComplete === true,
    });

    let requestDataUpdate = {
      startDate: start,
      teamPlanningId: planningId,
      teamId: teamId,
      endDate: end,
      workingDays: calculatedWorkingDays,
      planningStatusId: nextPlanningStatusId
    };

    updateTeamPlanning(requestDataUpdate);

  }

  useEffect(() => {
    if (teamId && planningId) {
      const req = {
        teamId: teamId,
        planningId: planningId,
      }
      getResourceTotalPlanningCount(req);
    }
  }, [teamId, planningId])

  return (
    <>
      {
        dataSource && (
          <div className="card-section-info card-data center-top-space">
            <div className="key-value-pair">
              <div className="key-value-pair-key">
                <Iconify icon="tdesign:member" width={15} />
                Team
              </div>
              : <div className="key-value-pair-value">{dataSource?.teamName}</div>
            </div>
            {/* <div className="key-value-pair">
              <div className="key-value-pair-key">
                <Iconify icon="carbon:calendar" width={17} />
                Planning Week
              </div>
              : <div className="key-value-pair-value startdate">
                {dataSource?.planningWeek}
                {formatDate(startDate, "DD MMM, YYYY")} - {formatDate(endDate,"DD MMM, YYYY")}`
              </div>
   
            </div> */}
            <div className="key-value-pair">
              <div className="key-value-pair-key">
                <Iconify icon="fluent:task-list-rtl-20-filled" width={17} />
                Total no. of tasks
              </div>
              : <div className="key-value-pair-value">{dataSource?.totalTask}</div>
            </div>
            <div className="key-value-pair">
              <div className="key-value-pair-key">
                <Iconify icon="lucide:clock" width={15} />
                Total Hrs
              </div>
              : <div className="key-value-pair-value">
                {Number(dataSource.totalWorkingHours) ? Number(dataSource.totalWorkingHours).toFixed(2) : "0.00"} hrs
              </div>
            </div>
            <div className="key-value-pair">
              <div className="key-value-pair-key">
                <Iconify icon="ph:clock-user" width={16} />
                Productive Hrs
              </div>
              : <div className="key-value-pair-value">
                {Number(dataSource?.totalProductiveHours) ? Number(dataSource.totalProductiveHours).toFixed(2) : "0.00"} hrs
              </div>
            </div>
            <div className="key-value-pair">
              <div className="key-value-pair-key">
                <Iconify icon="mdi:progress-clock" width={16} />
                Indirect Hrs
              </div>
              : <div className="key-value-pair-value">
                {Number(dataSource?.totalIndirectHours) ? Number(dataSource.totalIndirectHours).toFixed(2) : "0.00"} hrs
              </div>
            </div>
            <div className="key-value-pair">
              <div className="key-value-pair-key">
                <Iconify icon="fluent:person-clock-20-regular" width={18} />
                Actual Hrs
              </div>
              : <div className="key-value-pair-value">
                {Number(dataSource?.totalActualHours) ? Number(dataSource.totalActualHours).toFixed(2) : "0.00"} hrs
              </div>
            </div>
          </div>
        )
      }
      <div className="card-section-info card-data center-top-space">
        {isOverAllocated && (
          <div className="key-value-pair w-100">
            <div className="key-value-pair-key">
              <Iconify icon="mdi:alert-outline" width={17} />
              Warning
            </div>
            : <div className="key-value-pair-value">Estimated hours exceed productive hours.</div>
          </div>
        )}
        <div className="key-value-pair task-plan-key">
          <div className="key-value-pair-key">
            <Iconify icon="carbon:calendar" width={17} />
            Planning Week
          </div>
          : <div className="key-value-pair-value startdate">
            {isEditing && !isPlanComplete ? (
              <>
                <DatePickerComponent
                  isDisable={false}
                  selected={editedStartDate ? new Date(editedStartDate) : null}
                  onChange={(date) => setEditedStartDate(date)}
                  placeholder={"Select Start Date"}
                  datePickerConfig={{
                    filteredDate: () => true,
                    timeInputLabel: "Start Date:",
                    timeFormat: "p",
                    timeIntervals: 15,
                    format: "dd/MM/yyyy",
                  }}
                />
                -
                <DatePickerComponent
                  isDisable={false}
                  selected={editedEndDate ? new Date(editedEndDate) : null}
                  onChange={(date) => setEditedEndDate(date)}
                  placeholder={"Select End Date"}
                  datePickerConfig={{
                    filteredDate: () => true,
                    timeInputLabel: "End Date:",
                    timeFormat: "p",
                    timeIntervals: 15,
                    format: "dd/MM/yyyy",
                  }}
                />
              </>
            ) : (
              `${formatDate(editedStartDate, "DD MMM, YYYY")} - ${formatDate(editedEndDate, "DD MMM, YYYY")}`
            )}
          </div>

          <div className="action-buttons">
            {isEditing ? (
              <div className="edit-buttons">
                <button className="save-btn" onClick={() => handleSave(null)} aria-label="Save">
                  <Iconify icon="mdi:tick" />
                </button>
                <button className="cancel-btn" onClick={handleCancel} aria-label="Cancel">
                  <Iconify icon="iconoir:cancel" />
                </button>
              </div>
            ) : (
              !isPlanComplete && <button className="task-icon" onClick={handleEditClick} aria-label="Edit Planning Week">
                <Iconify icon="line-md:pencil" />
              </button>
            )}
          </div>
          <div className="key-value-pair ml-1">
            <div className="key-value-pair-key">
              <Iconify icon="ph:clock-user" width={16} />
              working Days
            </div>
            : <div className="key-value-pair-value">
              {displayWorkingDays} Day
            </div>
          </div>
          <div className="key-value-pair ml-2">
            <div className="key-value-pair-key">
              <Iconify icon="carbon:task-view" />
              Planning Status
            </div>
            <DropdownSelect
              options={listData}
              value={selectStatus}
              onChange={handleStatusChange}
              placeholder="Select Status"
            // onBlur={handleSave}
            />
          </div>
        </div>

      </div>


      <div className="center-top-space manage-breadcrumb-list center-top-space">
        <div className="render-tabs-card">
          <RenderTabs tabs={tabs} />
        </div>
      </div>
    </>
  );
};

export default TaskPlanTab;
